import { useState } from 'react';
import { useToast } from '../../../Context/ToastContext';

/**
 * Custom hook for handling macro actions like getting conversation text 
 * and sending to chatbot
 * @returns {Object} Functions for macro actions
 */
export const useMacroActions = () => {
  const [lastConversation, setLastConversation] = useState('');
  const { showToast } = useToast();

  /**
   * Gets the conversation text from the DOM
   * @returns {string} The conversation text
   */
  const getConversationText = () => {
    // Try multiple approaches to find conversation messages in various tabs
    
    // Try to get messages from MessageList in ConversationView
    const messageItems = document.querySelectorAll('.message-item');
    if (messageItems.length > 0) {
      console.log('Found messages via .message-item');
      const text = Array.from(messageItems).map(node => node.innerText || node.textContent || '').join('\n\n');
      setLastConversation(text);
      return text;
    }
    
    // Try to get messages from MessageList with chat message format
    const chatMessages = document.querySelectorAll('div[class*="ChatMessage"], [data-message], .message-container');
    if (chatMessages.length > 0) {
      console.log('Found messages via chat message selectors');
      const text = Array.from(chatMessages).map(node => node.innerText || node.textContent || '').join('\n\n');
      setLastConversation(text);
      return text;
    }
    
    // Try to get messages from conversation tab in UserList
    const userListMessages = document.querySelectorAll('.message-container, .system-message');
    if (userListMessages.length > 0) {
      console.log('Found messages via UserList message containers');
      const text = Array.from(userListMessages).map(node => node.innerText || node.textContent || '').join('\n\n');
      setLastConversation(text);
      return text;
    }

    // Fallback: try to get ANY element that might contain messages
    // Look for elements that have conversation-like text
    const allPotentialContainers = document.querySelectorAll('[class*="message"], [class*="Message"], [class*="chat"], [class*="Chat"]');
    if (allPotentialContainers.length > 0) {
      console.log('Found messages via potential containers');
      const text = Array.from(allPotentialContainers).map(node => node.innerText || node.textContent || '').join('\n\n');
      setLastConversation(text);
      return text;
    }

    // Last resort - try to find any element in tabs that might be a message list
    const tabPanels = document.querySelectorAll('[role="tabpanel"], [class*="TabPanel"], [class*="Content"]');
    for (const panel of tabPanels) {
      if (panel.innerText && panel.innerText.includes(':') && 
        (panel.innerText.toLowerCase().includes('you:') || panel.innerText.toLowerCase().includes('user:') || 
          panel.innerText.toLowerCase().includes('ai:') || panel.innerText.toLowerCase().includes('assistant:'))) {
        console.log('Found messages via tab panel content');
        const text = panel.innerText;
        setLastConversation(text);
        return text;
      }
    }
    
    // If all else fails, check if we have a selected conversation
    const selectedConversation = document.querySelector('[class*="selected"], [class*="active"]');
    if (selectedConversation) {
      showToast('A conversation appears to be selected, but the messages could not be found. Please click on the conversation tab first before using this feature.', 'warning');
    } else {
      showToast('No conversation found. Please select a user from the list first.', 'warning');
    }
    
    return '';
  };

  /**
   * Gets the summary text from the TextEditor
   * @returns {string} The summary text
   */
  const getSummaryText = () => {
    const editor = document.querySelector('[contenteditable="true"]');
    if (editor) {
      return editor.innerText || editor.textContent || '';
    }
    return '';
  };

  /**
   * Sends a message to the chatbot
   * @param {string} message - The message to send
   */
  const sendToChatbot = (message) => {
    const chatbotInput = document.querySelector('.chatbot-panel input[type="text"], input[placeholder*="Type your message"]');
    if (chatbotInput) {
      chatbotInput.value = message;
      chatbotInput.focus();
      chatbotInput.dispatchEvent(new Event('input', { bubbles: true }));
      const form = chatbotInput.closest('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true }));
      }
    } else {
      window.dispatchEvent(new CustomEvent('send-to-chatbot', { detail: { message } }));
    }
  };

  /**
   * Creates a directive with conversation
   * @param {string} directive - The directive template
   * @returns {string} The complete directive with conversation
   */
  const createDirectiveWithConversation = (directive) => {
    const conversation = getConversationText();
    if (!conversation) {
      return ''; // Alert is already shown in getConversationText
    }
    return `${directive}\n\nConversation:\n${conversation}`;
  };

  /**
   * Creates a directive with summary
   * @param {string} directive - The directive template
   * @returns {string} The complete directive with summary
   */
  const createDirectiveWithSummary = (directive) => {
    const summary = getSummaryText();
    if (!summary) {
      showToast('No summary found in the editor.', 'warning');
      return '';
    }
    return `${directive}\n\n${summary}`;
  };

  return {
    getConversationText,
    getSummaryText,
    sendToChatbot,
    createDirectiveWithConversation,
    createDirectiveWithSummary,
    lastConversation
  };
};

export default useMacroActions; 